import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadBCGData } from '@/api/mentalHealth/uploadBCGData';
import { AnalysisParams } from '@/util/bcg/types';
import BaseModal from '@/component/modal/BaseModal';
import SolidButton from '@/component/button/SolidButton';
import BorderButton from '@/component/button/BorderButton';
import { toast } from 'react-toastify';

interface BCGUploadModalProps {
  userId?: number;
  userName?: string;
  onSuccess?: (results: any) => void;
}

export interface BCGUploadModalRef {
  show: () => void;
  close: () => void;
}

const BCGUploadModal = forwardRef<BCGUploadModalRef, BCGUploadModalProps>(({
  userId,
  userName,
  onSuccess,
}, ref) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [params, setParams] = useState<AnalysisParams>({
    windowSec: 60,
    strideSec: 15,
    mode: 'absolute',
    calibrationPairs: [],
  });
  const [calibrationFile, setCalibrationFile] = useState<File | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const calibrationInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<any>(null);

  const uploadMutation = useUploadBCGData();

  useImperativeHandle(ref, () => ({
    show: () => modalRef.current?.show(),
    close: () => modalRef.current?.close(),
  }));

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    processFiles(selectedFiles);
  };

  const processFiles = (selectedFiles: File[]) => {
    const csvFiles = selectedFiles.filter(file => 
      file.name.toLowerCase().endsWith('.csv')
    );
    
    if (csvFiles.length !== selectedFiles.length) {
      toast.warning('CSV 파일만 업로드할 수 있습니다.');
    }
    
    if (csvFiles.length === 0) {
      toast.error('CSV 파일을 선택해주세요.');
      return;
    }
    
    // console.log('선택된 파일들:', csvFiles.map(f => `${f.name} (${f.size} bytes)`));
    setFiles(csvFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const handleCalibrationFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.json')) {
      setCalibrationFile(file);
      
      // JSON 파일 읽기
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          const pairs = data.pairs || [];
          setParams(prev => ({ ...prev, calibrationPairs: pairs }));
          toast.success(t('mentalHealth.modal.calibration.success'));
        } catch (error) {
          toast.error(t('mentalHealth.modal.calibration.error'));
          setCalibrationFile(null);
        }
      };
      reader.readAsText(file);
    } else {
      toast.warning('JSON 파일만 업로드할 수 있습니다.');
    }
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      toast.error(t('mentalHealth.modal.error.noFile'));
      return;
    }

    if (!userId || !userName) {
      toast.error(t('mentalHealth.modal.error.noMember'));
      return;
    }

    try {
      const result = await uploadMutation.mutateAsync({ 
        files, 
        params,
        userId,
        userName
      });
      
      // 에러가 있는 파일과 성공한 파일 분리
      const successReports = result.reports.filter(r => !r.error);
      const errorReports = result.reports.filter(r => r.error);
      
      if (successReports.length > 0) {
        toast.success(t('mentalHealth.modal.result.success', { count: successReports.length }));
        
        // onSuccess 콜백 먼저 호출 (대시보드 새로고침)
        onSuccess?.(result);
        
        // 분석 완료 즉시 모달 닫기
        handleCloseModal();
      }
      
      if (errorReports.length > 0) {
        errorReports.forEach(report => {
          toast.error(`${report.filename}: ${report.error}`);
        });
      }
    } catch (error) {
      toast.error(t('mentalHealth.modal.error.analysis'));
    }
  };

  const handleClose = () => {
    // 상태 초기화만 수행 (모달 닫기는 BaseModal이 처리)
    setFiles([]);
    setParams({
      windowSec: 60,
      strideSec: 15,
      mode: 'absolute',
      calibrationPairs: [],
    });
    setCalibrationFile(null);
    setAnalysisResults(null);
    setShowResults(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (calibrationInputRef.current) calibrationInputRef.current.value = '';
  };

  const handleCloseModal = () => {
    // 모달 닫기 (상태 초기화는 onCloseCallback에서 자동 호출됨)
    modalRef.current?.close();
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <BaseModal ref={modalRef} onCloseCallback={handleClose} width="800" showCloseButton={false}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold">
              {showResults ? t('mentalHealth.modal.result.title') : t('mentalHealth.modal.title')}
            </h2>
            {userName && (
              <p className="text-sm text-gray-600 mt-1">
                {t('mentalHealth.modal.targetMember')}: <strong>{userName}</strong>
              </p>
            )}
          </div>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {showResults && analysisResults ? (
          // 분석 결과 표시
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">{t('mentalHealth.modal.result.complete')}</h3>
              <p className="text-green-700">
                {analysisResults.reports.filter((r: any) => !r.error).length}개 파일이 성공적으로 분석되었습니다.
              </p>
            </div>

            <div className="space-y-3">
              {analysisResults.reports.map((report: any, index: number) => (
                <div key={index} className={`p-4 rounded-lg border ${
                  report.error ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{report.filename}</h4>
                      {report.error ? (
                        <p className="text-red-600 text-sm mt-1">{report.error}</p>
                      ) : (
                        <div className="text-sm text-gray-600 mt-1">
                          <p>{t('mentalHealth.modal.result.mindScore')}: <span className="font-medium">{report.report.mindScore?.score?.toFixed(1) || 'N/A'}</span></p>
                          <p>{t('mentalHealth.modal.result.status')}: <span className="font-medium">{report.report.decisionLabel || 'N/A'}</span></p>
                          <p>{t('mentalHealth.modal.result.stress')}: <span className="font-medium">{report.report.stressAnalysis?.category || 'N/A'}</span></p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <BorderButton 
                text={t('mentalHealth.modal.button.newAnalysis')}
                onClick={() => setShowResults(false)}
              />
              <SolidButton 
                text={t('mentalHealth.modal.button.done')}
                onClick={handleCloseModal}
              />
            </div>
          </div>
        ) : (
          // 기존 업로드 폼
          <div>
        
        {/* 파일 업로드 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('mentalHealth.modal.selectFile')}
          </label>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragOver 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="space-y-2">
              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  {t('mentalHealth.modal.selectFile.button')}
                </button>
                <span className="ml-1">{t('mentalHealth.modal.selectFile.drag')}</span>
              </div>
              <p className="text-xs text-gray-500">{t('mentalHealth.modal.selectFile.support')}</p>
            </div>
          </div>
          
          {files.length > 0 && (
            <div className="mt-3 space-y-2">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    {t('mentalHealth.modal.file.remove')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 분석 파라미터 */}
        <div className="mb-6 space-y-4">
          <h3 className="text-lg font-medium">{t('mentalHealth.modal.settings')}</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('mentalHealth.modal.window.size')}
              </label>
              <input
                type="number"
                min="30"
                max="120"
                step="15"
                value={params.windowSec}
                onChange={(e) => setParams(prev => ({ ...prev, windowSec: Number(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('mentalHealth.modal.stride')}
              </label>
              <input
                type="number"
                min="5"
                max="60"
                step="5"
                value={params.strideSec}
                onChange={(e) => setParams(prev => ({ ...prev, strideSec: Number(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              스코어링 모드
            </label>
            <select
              value={params.mode}
              onChange={(e) => setParams(prev => ({ ...prev, mode: e.target.value as 'absolute' | 'relative' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="absolute">절대 (최적 5분)</option>
              <option value="relative">상대 (비보정)</option>
            </select>
          </div>
        </div>

        {/* 캘리브레이션 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('mentalHealth.modal.calibration')}
          </label>
          <input
            ref={calibrationInputRef}
            type="file"
            accept=".json"
            onChange={handleCalibrationFileSelect}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          />
          {calibrationFile && (
            <div className="mt-2 text-sm text-green-600">
              ✓ {calibrationFile.name} 로드됨
            </div>
          )}
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <BorderButton 
            text={t('mentalHealth.modal.button.cancel')}
            onClick={handleCloseModal} 
          />
          <SolidButton 
            text={uploadMutation.isPending ? t('mentalHealth.modal.button.analyzing') : t('mentalHealth.modal.button.analyze')}
            onClick={handleSubmit} 
            disabled={files.length === 0 || uploadMutation.isPending}
          />
        </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
});

export default BCGUploadModal;
